/**
 * Typed Translation Hook
 *
 * Wraps react-i18next's useTranslation hook with type-safe key validation.
 * Only accepts valid TranslationKey values, catching typos at compile time.
 *
 * @module i18n/useTypedTranslation
 * @see Requirements 4.2
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TranslationKey } from './types';

/**
 * Options for translation interpolation.
 */
export interface TranslationOptions {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Return type for useTypedTranslation hook.
 */
export interface TypedTranslationResult {
  /**
   * Type-safe translation function.
   * Only accepts valid TranslationKey values.
   *
   * @param key - A valid translation key from en.json
   * @param options - Optional interpolation values
   * @returns The translated string
   */
  t: (key: TranslationKey, options?: TranslationOptions) => string;

  /**
   * The i18next instance for advanced operations.
   */
  i18n: ReturnType<typeof useTranslation>['i18n'];

  /**
   * Whether translations are ready.
   */
  ready: boolean;
}

/**
 * Type-safe translation hook wrapping react-i18next.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { t } = useTypedTranslation();
 *   return <div>{t("common.loading")}</div>;
 * }
 * ```
 *
 * @returns Object containing type-safe translation function and i18n instance
 */
export function useTypedTranslation(): TypedTranslationResult {
  const { t, i18n, ready } = useTranslation();

  // Use useCallback to ensure stable function reference across renders
  // This prevents infinite re-renders when t is used in useEffect dependencies
  const typedT = useCallback(
    (key: TranslationKey, options?: TranslationOptions): string => {
      return t(key, options as Record<string, unknown>);
    },
    [t]
  );

  return { t: typedT, i18n, ready };
}
